import { Core } from "@faire/design-tokens";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { useFetchUser } from "@faire/retailer-visitor-shared/stores/domain/useUserUpdate";
import { getWebApiErrorMessage } from "@faire/web--source/common/getErrorMessage";
import { uploadImageFile } from "@faire/web--source/common/images/uploadImageFile";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { ImageUploader } from "@faire/web--source/slate/ImageUploader/ImageUploader";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Modal } from "@faire/web--source/slate/Modal";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useMutation as useMutationPatchUser } from "@faire/web-api--source/endpoints/www/api/user/patch-hooks";
import { trackUpmarketMultiUserEducationBannerProfilePictureUpdateSubmit } from "@faire/web-api--source/events/upmarket/submit/multiUserEducationBannerProfilePictureUpdate";
import { trackUpmarketMultiUserEducationBannerProfilePictureUploadSubmit } from "@faire/web-api--source/events/upmarket/submit/multiUserEducationBannerProfilePictureUpload";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import * as React from "react";
import styled from "styled-components";

interface IProps {
  onClose: () => void;
  onProfilePictureUpdated?: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const ProfilePictureUploadModal: React.FC<IProps> = ({
  onClose,
  onProfilePictureUpdated,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [image, setImage] = React.useState<IImage | undefined>(undefined);
  const { userToken } = useUserStore(["userToken"]);
  const fetchUser = useFetchUser();

  const { mutate: updateUser, isPending: isSavingProfilePicture } =
    useMutationPatchUser({
      onSuccess: () => {
        onProfilePictureUpdated?.();
        fetchUser();
        onClose();
      },
      onError: (error) => {
        setError(getWebApiErrorMessage(error, strictLocalize));
      },
    });

  const handleFileUpload = async (file: File) => {
    try {
      const image = await uploadImageFile(file);
      if (image.token && image.url) {
        trackUpmarketMultiUserEducationBannerProfilePictureUploadSubmit();
        setImage(image);
      }
    } catch (error) {
      const errorMessage = getWebApiErrorMessage(error, strictLocalize);
      setError(errorMessage);
    }
  };

  const handleUploadError = (message: string) => {
    setError(message);
  };

  const handleProfilePictureSave = async () => {
    trackUpmarketMultiUserEducationBannerProfilePictureUpdateSubmit();
    const updatedUser = {
      token: userToken,
      profile_image: image,
    };
    updateUser([updatedUser]);
  };

  const hasUploadedImage = !!image;

  return (
    <Modal
      width="383px"
      height="auto"
      justify="flex-start"
      align="flex-start"
      padding={getSpacing("3x")}
      onClose={onClose}
      data-test-id="profile-picture-upload-modal"
    >
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          defaultMessage="Upload a photo"
          description={{
            text: "title of a modal that updates the user's profile picture",
            img: "https://cdn.faire.com/fastly/c42b64ca418c14ca967b6368131feed6df43808efb5a4926c3dbf885eef56fcc.png",
          }}
        />
      </Typography>
      <Spacer height="4x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="We’ll use this to represent your activity to your teammates and brands throughout the site."
          description={{
            text: "modal description explaining how the user's profile picture will be used when they upload a new one",
            img: "https://cdn.faire.com/fastly/c42b64ca418c14ca967b6368131feed6df43808efb5a4926c3dbf885eef56fcc.png",
          }}
        />
      </Typography>
      <Spacer height="3x" />
      <ContentWrapper>
        <ImageUploader
          width={200}
          height={200}
          currentImage={image}
          onFileUpload={handleFileUpload}
          uploadCta={strictLocalize({
            defaultMessage: "Upload JPEG, JPG, PNG, or WEBP",
            description: {
              text: "button label for uploading a new profile picture",
              img: "https://cdn.faire.com/fastly/c42b64ca418c14ca967b6368131feed6df43808efb5a4926c3dbf885eef56fcc.png",
            },
          })}
          maxFileSize={MAX_FILE_SIZE}
          circular={hasUploadedImage}
          error={!!error}
          onUploadError={handleUploadError}
        />
        <Spacer height="1x" />
        <Typography variant="labelSansRegular">
          <StrictLocalMsg
            defaultMessage="Photo must be less than 10MB"
            description={{
              text: "image upload button warning",
              img: "https://cdn.faire.com/fastly/c42b64ca418c14ca967b6368131feed6df43808efb5a4926c3dbf885eef56fcc.png",
            }}
          />
        </Typography>
      </ContentWrapper>
      {error ? (
        <>
          <Spacer height="1x" />
          <Typography variant="labelSansRegular" color={Core.icon.critical}>
            {error}
          </Typography>
        </>
      ) : null}
      <Spacer height="4x" />
      <ButtonWrapper>
        <StyledButton
          disabled={!hasUploadedImage}
          onClick={handleProfilePictureSave}
          loading={isSavingProfilePicture}
        >
          <StrictLocalMsg
            defaultMessage="Save"
            description={{
              text: "save button label",
              img: "https://cdn.faire.com/fastly/c42b64ca418c14ca967b6368131feed6df43808efb5a4926c3dbf885eef56fcc.png",
            }}
          />
        </StyledButton>
        <Spacer width="2x" />
        <StyledButton variant="secondary" onClick={onClose}>
          <StrictLocalMsg
            defaultMessage="Cancel"
            description={{
              text: "cancel button label",
              img: "https://cdn.faire.com/fastly/c42b64ca418c14ca967b6368131feed6df43808efb5a4926c3dbf885eef56fcc.png",
            }}
          />
        </StyledButton>
      </ButtonWrapper>
    </Modal>
  );
};

const ContentWrapper = styled(Column).attrs({ align: "center" })`
  width: 100%;
`;

const ButtonWrapper = styled(Row)`
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;
